import React, { useEffect, useContext } from "react"
import styled from "styled-components"
import { useLocation } from "@reach/router"
import { useCookies } from "react-cookie"
import { initializeAndTrack } from "gatsby-plugin-gdpr-cookies"
import { BrinkContext } from "../context/BrinkContext"
import { useTranslation } from "react-i18next"
import { boxShadow, MEDIA_MIN_MEDIUM } from "../../constants"
import Button from "../ui/Button"
import dayjs from "dayjs"

const Container = styled.div`
  position: fixed;
  bottom: 2rem;
  right: 2rem;
  left: 2rem;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  justify-content: center;
  color: ${(p) => p.theme.colors.black};
  background: ${(p) => p.theme.colors.backgroundDark};
  padding: 1.6rem 2rem 2.4rem;
  box-shadow: ${boxShadow};
  transition: opacity 0.3s;
  z-index: 1000;
  font-size: 1.3rem;
  max-width: 85rem;
  line-height: 2rem;

  p {
    margin: 0.5rem 0 2rem;
    text-align: center;
  }

  a {
    margin-left: 0.5rem;
    white-space: nowrap;
  }

  ${MEDIA_MIN_MEDIUM} {
    padding: 2.5rem 2.5rem 2.8rem;
    left: auto;
    flex-wrap: nowrap;
    font-size: 1.4rem;
    line-height: 2.4rem;
    justify-content: flex-start;

    p {
      padding-right: 2rem;
      margin: 1rem 0;
      text-align: left;
    }
  }
`

const Actions = styled.div`
  display: flex;
  justify-content: center;
  flex-wrap: nowrap;
  align-items: center;

  ${MEDIA_MIN_MEDIUM} {
    margin-left: 2rem;
  }
`

const AcceptButton = styled(Button)`
  height: 4rem;
  border: 0.1rem solid ${(p) => p.theme.colors.white};
  margin: 0 auto;
  color: ${(p) => p.theme.colors.white};
  white-space: nowrap;
  padding-right: 2.5rem;

  i {
    font-size: 1.6rem;
    position: relative;
    right: 0.5rem;
  }
`

const SettingsLink = styled.button`
  margin-left: 1rem;
  font-size: 1.1rem;
  background: none;
  display: flex;
  border: none;
  color: ${(p) => p.theme.colors.black};
  position: relative;
  padding: 0;
  cursor: pointer;
  white-space: nowrap;

  &:after {
    content: "";
    position: absolute;
    height: 1px;
    left: 0;
    right: 0;
    bottom: -0.3rem;
    background-color: ${(p) => p.theme.colors.black};
    transition: background-color 0.2s;
  }

  &:hover {
    &:after {
      background-color: transparent;
    }
  }
`

const NoticeBanner = ({ bannerVisible, setBannerVisible }) => {
  const { t } = useTranslation("translations")
  const { setCookieSettingsVisible } = useContext(BrinkContext)
  const [cookies, setCookie] = useCookies("cookieBanner")
  const location = useLocation()

  useEffect(() => {
    if (!cookies.cookieBanner) setBannerVisible(true)
  }, [cookies, setBannerVisible])

  return (
    bannerVisible && (
      <Container>
        <p>
          {t(
            "By accepting, you agree to the storing of cookies to improve site experience."
          )}
        </p>
        <Actions>
          <AcceptButton
            onClick={() => {
              setCookieSettingsVisible(false)
              setBannerVisible(false)
              setCookie("cookieBanner", "1", {
                path: "/",
                expires: dayjs().add(180, "days").toDate()
              })
              setCookie("gdprGoogleAnalytics", "true", {
                path: "/",
                expires: dayjs().add(180, "days").toDate()
              })
              initializeAndTrack(location)
            }}
          >
            <i className="fal fa-check"></i> {t("Accept all")}
          </AcceptButton>
          <SettingsLink
            onClick={(e) => {
              e.preventDefault()
              setCookieSettingsVisible(true)
            }}
          >
            {t("Cookie settings")}
          </SettingsLink>
        </Actions>
      </Container>
    )
  )
}

export default NoticeBanner
